import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

interface LanguageToggleProps {
  variant?: "header" | "footer";
  className?: string;
}

const LanguageToggle = ({ variant = "header", className = "" }: LanguageToggleProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentLang = i18n.language?.substring(0, 2) || "en";

  const switchLanguage = (lang: string) => {
    if (lang === currentLang) return;
    
    // Get current path without language prefix
    const pathParts = location.pathname.split("/").filter(Boolean);
    const hasLangPrefix = pathParts[0] === "en" || pathParts[0] === "fr";
    const pathWithoutLang = hasLangPrefix ? "/" + pathParts.slice(1).join("/") : location.pathname;
    
    // Navigate to new language path
    const newPath = `/${lang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    i18n.changeLanguage(lang);
    navigate(newPath);
  };

  const baseStyles = variant === "header" 
    ? "text-xs uppercase tracking-wide font-medium transition-colors"
    : "text-sm transition-colors";

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      <button
        onClick={() => switchLanguage("en")}
        className={`${baseStyles} px-2 py-1 border-b-2 ${
          currentLang === "en" 
            ? "text-primary border-primary" 
            : "text-muted-foreground hover:text-primary border-transparent"
        }`}
        aria-label="Switch to English"
        aria-pressed={currentLang === "en"}
      >
        {t("language.en")}
      </button>
      <span className="text-muted-foreground/50" aria-hidden="true">|</span>
      <button
        onClick={() => switchLanguage("fr")}
        className={`${baseStyles} px-2 py-1 border-b-2 ${
          currentLang === "fr" 
            ? "text-primary border-primary" 
            : "text-muted-foreground hover:text-primary border-transparent"
        }`}
        aria-label="Passer au français"
        aria-pressed={currentLang === "fr"}
      >
        {t("language.fr")}
      </button>
    </div>
  );
};

export default LanguageToggle;
